import { Service, computed, effect, signal } from '@angular/core';

/** Cores disponíveis em src/css/themes.css (o atributo data-theme do <html>). */
export type CorTema =
  | 'light'
  | 'grey'
  | 'blue'
  | 'green'
  | 'red'
  | 'yellow'
  | 'pink'
  | 'ciano'
  | 'purple';

export const CORES_TEMA: readonly CorTema[] = [
  'grey',
  'blue',
  'green',
  'red',
  'yellow',
  'pink',
  'ciano',
  'purple',
];

const COR_PADRAO: CorTema = 'light';

/** A cor de uma chave de tema vinda da API, ou `null` quando não é uma das cores do portal. */
export function corDoTema(chave: string | null | undefined): CorTema | null {
  return CORES_TEMA.find((cor) => cor === chave) ?? null;
}

/**
 * Estado único do tema da aplicação.
 * Escreve o atributo `data-theme` no <html>, que é o gancho usado por src/css/themes.css.
 *
 * Há duas cores: a do órgão, que o adm publica (Padrão de Cores e Ordenação), e a que o
 * visitante escolhe no portal. A do visitante prevalece enquanto ele não restaurar — mesmo
 * quando a do órgão chega depois da escolha.
 */
@Service()
export class TemaService {

  private readonly corOrgao = signal<CorTema>(COR_PADRAO);
  private readonly corVisitante = signal<CorTema | null>(null);
  private readonly escuro = signal(false);

  /** A cor em uso: a do visitante, senão a do órgão. É a que o seletor marca. */
  readonly corSelecionada = computed<CorTema>(() => this.corVisitante() ?? this.corOrgao());
  readonly modoEscuro = this.escuro.asReadonly();

  /** Valor efetivo do data-theme: o modo escuro sobrepõe a cor selecionada. */
  readonly temaAtivo = computed<CorTema | 'dark'>(() => this.escuro() ? 'dark' : this.corSelecionada());

  constructor() {
    effect(() => document.documentElement.setAttribute('data-theme', this.temaAtivo()));
  }

  /** A escolha do visitante. */
  definirCor(cor: CorTema) {
    this.corVisitante.set(cor);
  }

  /** O tema que o órgão publica. Chave nula ou desconhecida mantém a cor padrão do portal. */
  definirCorOrgao(chave: string | null | undefined) {
    this.corOrgao.set(corDoTema(chave) ?? COR_PADRAO);
  }

  alternarModoEscuro() {
    this.escuro.update(escuro => !escuro);
  }

  /** Volta ao tema padrão: a cor do órgão e o modo escuro desligado. */
  restaurarCor() {
    this.corVisitante.set(null);
    this.escuro.set(false);
  }
}
